import { apiCall } from "./Common";
import { replaceWidgets } from "../utils/widgetLocalStorage";

let widgetsSessionCache = null;
let widgetsLoadPromise = null;

export function getCachedWidgets() {
  return widgetsSessionCache;
}

export function isWidgetsCacheReady() {
  return widgetsSessionCache !== null;
}

export function setCachedWidgets(list) {
  widgetsSessionCache = Array.isArray(list) ? list : [];
  replaceWidgets(widgetsSessionCache);
}

export function invalidateWidgetsCache() {
  widgetsSessionCache = null;
  widgetsLoadPromise = null;
}

export async function fetchWidgetsFromDb(options = {}) {
  const force = options === true || options?.force === true;

  if (!force && widgetsSessionCache) {
    return widgetsSessionCache;
  }

  if (!force && widgetsLoadPromise) {
    return widgetsLoadPromise;
  }

  const promise = (async () => {
    const res = await apiCall("widgets", "GET");
    if (!res?.success) {
      throw new Error(res?.message || "Failed to load widgets");
    }
    const list = Array.isArray(res.data) ? res.data : [];
    setCachedWidgets(list);
    return list;
  })();

  widgetsLoadPromise = promise;
  try {
    return await promise;
  } finally {
    widgetsLoadPromise = null;
  }
}

export async function fetchWidgetFromDb(widgetId) {
  const cached = widgetsSessionCache?.find((w) => w.id === widgetId);
  if (cached) {
    return cached;
  }
  const res = await apiCall(`widgets/${encodeURIComponent(widgetId)}`, "GET");
  if (!res?.success) {
    throw new Error(res?.message || "Failed to load widget");
  }
  return res.data || null;
}

export async function saveWidgetToDb(widget) {
  if (!widget?.id) {
    throw new Error("Widget id is required");
  }
  const res = await apiCall(`widgets/${encodeURIComponent(widget.id)}`, "PUT", widget);
  if (!res?.success) {
    throw new Error(res?.message || "Failed to save widget");
  }
  const data = res.data || {};
  if (Array.isArray(data.widgets)) {
    setCachedWidgets(data.widgets);
  } else {
    invalidateWidgetsCache();
  }
  return data;
}

export async function deleteWidgetFromDb(widgetId) {
  const res = await apiCall(`widgets/${encodeURIComponent(widgetId)}`, "DELETE");
  if (!res?.success) {
    throw new Error(res?.message || "Failed to delete widget");
  }
  const data = res.data || {};
  if (Array.isArray(data.widgets)) {
    setCachedWidgets(data.widgets);
  } else {
    invalidateWidgetsCache();
  }
  return data;
}

/** Re-push all active widgets to shop metafield (install/reinstall recovery). */
export async function syncWidgetsMetafieldFromDb() {
  const res = await apiCall("widgets/sync-metafields", "POST");
  if (!res?.success) {
    throw new Error(res?.message || "Metafield sync failed");
  }
  return res.data || {};
}
